/**
 * OrbitPrint — the live channel (slice 5).
 *
 * One WebSocket per signed-in session to /api/orbit/ws. The server sends
 * content-free hints ({type:"refresh", what:"jobs"|"locations", job_id?});
 * the pages re-fetch through the normal permission-checked HTTP routes.
 * Polling stays as the fallback: every page keeps its timer, relaxed while the
 * line is open and tightened again the moment it drops.
 *
 * Auth is the FIRST FRAME ({type:"auth", token}), never the URL — the token
 * must not land in an access log. Reconnect backs off 1 s → 30 s; a 4401
 * close (dead session) stops reconnecting until the next sign-in.
 *
 * `state`: 'live' | 'connecting' | 'down'. The top bar shows it honestly
 * (owner rule: controls never lie) — "Live" or "Checking every N s".
 */
import { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { getActiveOrbitToken, useOrbitAuth } from './OrbitAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const MIN_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30000;
const CLOSE_UNAUTHORIZED = 4401;

export function wsBase() {
  if (BACKEND_URL) return BACKEND_URL.replace(/^http/, 'ws');
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}`;
}

const OrbitLiveContext = createContext({ state: 'down', subscribe: () => () => {} });

export function OrbitLiveProvider({ children }) {
  const { user, org } = useOrbitAuth();
  const [state, setState] = useState('down');
  const listeners = useRef(new Set());
  const wsRef = useRef(null);
  const backoff = useRef(MIN_BACKOFF_MS);
  const retryTimer = useRef(null);
  const stopped = useRef(false);

  const subscribe = useCallback((fn) => {
    listeners.current.add(fn);
    return () => listeners.current.delete(fn);
  }, []);

  useEffect(() => {
    const token = getActiveOrbitToken();        // a support session's token wins for this tab
    if (!user || !org?.id || !token || typeof WebSocket === 'undefined') { setState('down'); return undefined; }
    stopped.current = false;
    backoff.current = MIN_BACKOFF_MS;

    const connect = () => {
      if (stopped.current) return;
      setState('connecting');
      let ws;
      try { ws = new WebSocket(`${wsBase()}/api/orbit/ws`); } catch { setState('down'); scheduleRetry(); return; }
      wsRef.current = ws;
      ws.onopen = () => { ws.send(JSON.stringify({ type: 'auth', token: getActiveOrbitToken() || token })); };
      ws.onmessage = (ev) => {
        let msg;
        try { msg = JSON.parse(ev.data); } catch { return; }
        if (msg.type === 'hello') { backoff.current = MIN_BACKOFF_MS; setState('live'); return; }
        if (msg.type === 'ping') { try { ws.send(JSON.stringify({ type: 'pong' })); } catch { /* closing */ } return; }
        if (msg.type === 'refresh') listeners.current.forEach((fn) => { try { fn(msg); } catch { /* a listener must never kill the line */ } });
      };
      ws.onclose = (ev) => {
        if (wsRef.current === ws) wsRef.current = null;
        setState('down');
        if (ev.code === CLOSE_UNAUTHORIZED) { stopped.current = true; return; }   // dead session: the auth context handles it
        scheduleRetry();
      };
      ws.onerror = () => { try { ws.close(); } catch { /* already closed */ } };
    };
    const scheduleRetry = () => {
      if (stopped.current) return;
      clearTimeout(retryTimer.current);
      const wait = backoff.current;
      backoff.current = Math.min(MAX_BACKOFF_MS, backoff.current * 2);
      retryTimer.current = setTimeout(connect, wait);
    };
    const onVisible = () => {
      // a laptop coming back from sleep: don't wait out the backoff
      if (document.visibilityState === 'visible' && !wsRef.current && !stopped.current) { clearTimeout(retryTimer.current); backoff.current = MIN_BACKOFF_MS; connect(); }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onVisible);
    connect();
    return () => {
      stopped.current = true;
      clearTimeout(retryTimer.current);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onVisible);
      const ws = wsRef.current; wsRef.current = null;
      if (ws) { try { ws.close(); } catch { /* already closed */ } }
      setState('down');
    };
  }, [user, org?.id]);

  const value = useMemo(() => ({ state, subscribe }), [state, subscribe]);
  return <OrbitLiveContext.Provider value={value}>{children}</OrbitLiveContext.Provider>;
}

export function useOrbitLive() {
  return useContext(OrbitLiveContext);
}

/**
 * Re-run `cb` when the server says `what` changed (debounced so a burst of
 * hints costs one fetch). `filter` narrows further, e.g. to one job id.
 */
export function useLiveRefresh(what, cb, filter) {
  const { subscribe } = useOrbitLive();
  const cbRef = useRef(cb); cbRef.current = cb;
  const filterRef = useRef(filter); filterRef.current = filter;
  useEffect(() => {
    let t = null;
    const off = subscribe((msg) => {
      if (msg.what !== what) return;
      if (filterRef.current && !filterRef.current(msg)) return;
      clearTimeout(t);
      t = setTimeout(() => cbRef.current?.(msg), 250);
    });
    return () => { clearTimeout(t); off(); };
  }, [subscribe, what]);
}

/** Poll interval for a page: relaxed while the line is open, tight when it is not. */
export function usePollMs(liveMs, fallbackMs) {
  const { state } = useOrbitLive();
  return state === 'live' ? liveMs : fallbackMs;
}
